import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CommandContext, CommandHandler } from "./types";
import { getCommand } from "./registry";

interface CommandBridge {
  register: (id: string, handler: CommandHandler) => () => void;
  run: (id: string) => void;
  getContext: () => CommandContext;
  setContextOverrides: (overrides: Partial<CommandContext>) => () => void;
}

const CommandBridgeContext = createContext<CommandBridge | null>(null);

interface CommandProviderProps {
  context: CommandContext;
  children: ReactNode;
}

export function CommandProvider({ context, children }: CommandProviderProps) {
  const handlersRef = useRef(new Map<string, CommandHandler>());
  const [overrides, setOverrides] = useState<Partial<CommandContext>>({});

  const effectiveContext = useMemo<CommandContext>(
    () => ({ ...context, ...overrides }),
    [context, overrides],
  );

  const contextRef = useRef(effectiveContext);
  useEffect(() => {
    contextRef.current = effectiveContext;
  }, [effectiveContext]);

  const register = useCallback((id: string, handler: CommandHandler) => {
    handlersRef.current.set(id, handler);
    return () => {
      if (handlersRef.current.get(id) === handler) {
        handlersRef.current.delete(id);
      }
    };
  }, []);

  const run = useCallback((id: string) => {
    const handler = handlersRef.current.get(id);
    if (!handler) {
      const def = getCommand(id);
      if (!def) console.warn(`[commands] Unknown command id: ${id}`);
      else console.warn(`[commands] No handler registered for: ${id}`);
      return;
    }
    handler(contextRef.current);
  }, []);

  const getContext = useCallback(() => contextRef.current, []);

  const setContextOverrides = useCallback((next: Partial<CommandContext>) => {
    setOverrides((prev) => ({ ...prev, ...next }));
    return () => {
      setOverrides((prev) => {
        const updated = { ...prev };
        for (const key of Object.keys(next) as (keyof CommandContext)[]) {
          delete updated[key];
        }
        return updated;
      });
    };
  }, []);

  const bridge = useMemo<CommandBridge>(
    () => ({ register, run, getContext, setContextOverrides }),
    [register, run, getContext, setContextOverrides],
  );

  return (
    <CommandBridgeContext.Provider value={bridge}>
      {children}
    </CommandBridgeContext.Provider>
  );
}

function useCommandBridge(): CommandBridge {
  const bridge = useContext(CommandBridgeContext);
  if (!bridge) {
    throw new Error("CommandProvider is missing from the component tree");
  }
  return bridge;
}

/**
 * Registers `handler` as the runtime implementation for `commandId` for the
 * lifetime of the calling component. If multiple components register the same
 * command, the last one wins; unmounting that component does NOT restore a
 * previously-registered handler (it clears the slot).
 */
export function useRegisterCommand(
  commandId: string,
  handler: CommandHandler,
): void {
  const { register } = useCommandBridge();
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    return register(commandId, (ctx) => handlerRef.current(ctx));
  }, [commandId, register]);
}

/** Fire a command by id. */
export function useRunCommand(): (id: string) => void {
  return useCommandBridge().run;
}

/** Read the latest command context synchronously (outside React render). */
export function useCommandContext(): () => CommandContext {
  return useCommandBridge().getContext;
}
